import api from './api'
import type { BackfillAuditNormLine } from './cloverBackfillAudit'

export type BackfillApplyMode = 'dry_run' | 'live'

export type BackfillApplyRunStatus =
  | 'planned'
  | 'running'
  | 'waiting_for_clover_tokens'
  | 'completed'
  | 'completed_with_errors'
  | 'aborted'
  | 'failed'

export type BackfillApplyPath = 'update_existing_draft' | 'adopt'

export interface BackfillApplyReconJeSummary {
  draft_id: number
  qbo_id: string
  doc_number: string
  amount?: number | string
  amount_by_store: Record<string, number | string>
  sync_token?: string
  lines?: unknown[]
  deleted_at?: string | null
  inactive_in_qbo?: boolean
}

export interface BackfillApplyPlanRow {
  store: string
  date: string
  qbo_je_id: string
  doc_number_current: string | null
  doc_number_target: string | null
  path: BackfillApplyPath
  before: BackfillAuditNormLine[]
  after: BackfillAuditNormLine[]
  diff_summary: string | null
  delta_debits: number | string | null
  categories: string[]
  stale: string | null
  plan_decision?: string
}

export interface BackfillApplySkippedRow {
  store: string
  date: string
  reason: string
}

export interface BackfillApplyBlocker {
  store: string
  date: string
  reason: string
  detail?: string
}

export type BackfillApplyReconAction = 'delete' | 'none_needed' | 'blocked'

export interface BackfillApplyPlan {
  report_month: string
  store_names: string[]
  verify_rebuild: boolean
  verify_rebuild_warning?: boolean
  planned_at?: string
  rows: BackfillApplyPlanRow[]
  eligible?: BackfillApplyPlanRow[]
  skipped: BackfillApplySkippedRow[]
  blockers: BackfillApplyBlocker[]
  recon_je: BackfillApplyReconJeSummary | null
  recon_action?: BackfillApplyReconAction
  recon_deletable: boolean
  recon_none_needed_reason?: string | null
  recon_not_deletable_reason?: string | null
  tie_out_expected_after: {
    by_store?: Record<string, number | string>
    per_store?: Record<string, number | string>
    variance_before?: Record<string, number | string>
    recon_amount?: number | string
    cumulative_note?: string
  }
}

export interface BackfillApplyMonthSummary {
  month?: string
  report_month: string
  decision_counts: Record<string, number>
  apply_counts: Record<string, number>
  apply_status_counts?: Record<string, number>
  recon_je: {
    draft_id: number | null
    qbo_id: string | null
    doc_number: string
    amount?: number | string | null
    amount_by_store: Record<string, number | string>
  } | null
  last_run: {
    id: number
    mode: BackfillApplyMode
    status: BackfillApplyRunStatus
    completed_at: string | null
  } | null
  nothing_to_apply?: boolean
}

export interface BackfillApplyRunResultRow {
  store: string
  date: string
  status?: string
  error?: string
}

export interface BackfillApplyRunResultRecon {
  action?: BackfillApplyReconAction
  deleted?: boolean
  reason?: string | null
}

export interface BackfillApplyRunResult {
  rows?: BackfillApplyRunResultRow[]
  applied?: number
  failed?: number
  skipped?: number
  recon?: BackfillApplyRunResultRecon
  residuals?: string[]
  recon_deleted?: boolean
  expected?: Record<string, string | number>
  actual?: Record<string, string | number>
  variance_before?: Record<string, string | number>
  month_ties?: boolean
  summary?: string
}

export interface BackfillApplyRun {
  id: number
  report_month: string
  store_names: string[]
  mode: BackfillApplyMode
  status: BackfillApplyRunStatus
  job_id: number | null
  plan: BackfillApplyPlan | null
  result: BackfillApplyRunResult | null
  recon_je: BackfillApplyReconJeSummary | null
  tie_out: Record<string, unknown> | null
  created_at: string
  started_at: string | null
  completed_at: string | null
  confirmed_by: string | null
  confirmation_text: string | null
  parent_run_id?: number | null
}

export interface BackfillApplyPlanRequest {
  report_month: string
  store_names?: string[]
  verify_rebuild?: boolean
  force?: boolean
}

export interface BackfillApplyPlanResponse {
  run_id: number
  job_id?: number
  plan?: BackfillApplyPlan
}

export interface BackfillApplyExecuteRequest {
  confirmation: string
}

export interface BackfillApplyExecuteResponse {
  run_id: number
  job_id: number
}

const BASE = '/clover-reports/backfill-apply'

type RawPlan = Omit<
  BackfillApplyPlan,
  'rows' | 'tie_out_expected_after' | 'recon_action' | 'recon_deletable'
> & {
  rows?: BackfillApplyPlanRow[]
  eligible?: BackfillApplyPlanRow[]
  recon_action?: BackfillApplyReconAction
  recon_deletable?: boolean
  tie_out_expected_after?: BackfillApplyPlan['tie_out_expected_after']
}

type RawMonth = Partial<BackfillApplyMonthSummary> & {
  month?: string
  apply_status_counts?: Record<string, number>
  recon_je_id?: string | null
  recon_je_amount?: string | null
}

const RECON_NONE_NEEDED_REASONS = new Set(['already_deleted', 'recon_je_not_found', 'inactive_in_qbo'])

function deriveReconAction(plan: RawPlan): BackfillApplyReconAction {
  if (plan.recon_action) return plan.recon_action
  if (plan.recon_deletable) return 'delete'
  const reason = plan.recon_none_needed_reason ?? plan.recon_not_deletable_reason ?? ''
  if (RECON_NONE_NEEDED_REASONS.has(reason)) return 'none_needed'
  return 'blocked'
}

export function normalizeBackfillApplyPlan(plan: RawPlan): BackfillApplyPlan {
  const rows = plan.rows ?? plan.eligible ?? []
  const tieOut = plan.tie_out_expected_after
  const tieOutExpectedAfter = {
    by_store: tieOut?.by_store,
    per_store: tieOut?.per_store ?? tieOut?.by_store,
    variance_before: tieOut?.variance_before,
    recon_amount: tieOut?.recon_amount,
    cumulative_note: tieOut?.cumulative_note,
  }
  const verifyRebuildWarning =
    plan.verify_rebuild_warning ??
    !plan.verify_rebuild
  const reconAction = deriveReconAction(plan)
  const reconDeletable = reconAction === 'delete'
  let reconNotDeletableReason = plan.recon_not_deletable_reason ?? null
  let reconNoneNeededReason = plan.recon_none_needed_reason ?? null
  if (reconAction === 'none_needed') {
    reconNoneNeededReason =
      reconNoneNeededReason ??
      (RECON_NONE_NEEDED_REASONS.has(reconNotDeletableReason ?? '') ? reconNotDeletableReason : null)
    reconNotDeletableReason = null
  } else if (reconAction === 'blocked' && !reconNotDeletableReason) {
    reconNotDeletableReason = 'open rows remain in this month'
  }
  return {
    ...plan,
    rows,
    skipped: plan.skipped ?? [],
    blockers: plan.blockers ?? [],
    recon_je: plan.recon_je ?? null,
    recon_action: reconAction,
    recon_deletable: reconDeletable,
    recon_none_needed_reason: reconNoneNeededReason,
    recon_not_deletable_reason: reconNotDeletableReason,
    tie_out_expected_after: tieOutExpectedAfter,
    verify_rebuild_warning: verifyRebuildWarning,
  }
}

export function normalizeBackfillApplyMonth(month: RawMonth): BackfillApplyMonthSummary {
  const reportMonth = month.report_month ?? month.month ?? ''
  return {
    month: month.month ?? reportMonth,
    report_month: reportMonth,
    decision_counts: month.decision_counts ?? {},
    apply_counts: month.apply_counts ?? month.apply_status_counts ?? {},
    apply_status_counts: month.apply_status_counts ?? month.apply_counts,
    recon_je: month.recon_je ?? null,
    last_run: month.last_run ?? null,
    nothing_to_apply: month.nothing_to_apply,
  }
}

export function normalizeBackfillApplyRun(run: BackfillApplyRun): BackfillApplyRun {
  return {
    ...run,
    plan: run.plan ? normalizeBackfillApplyPlan(run.plan) : null,
  }
}

export async function getBackfillApplyMonths(): Promise<{ months: BackfillApplyMonthSummary[] }> {
  const { data } = await api.get<{ months: RawMonth[] }>(`${BASE}/months`)
  return { months: (data.months ?? []).map(normalizeBackfillApplyMonth) }
}

export async function postBackfillApplyPlan(
  body: BackfillApplyPlanRequest,
): Promise<BackfillApplyPlanResponse> {
  const { data } = await api.post<BackfillApplyPlanResponse & { plan?: RawPlan }>(`${BASE}/plan`, body)
  return {
    ...data,
    plan: data.plan ? normalizeBackfillApplyPlan(data.plan) : undefined,
  }
}

export async function postBackfillApplyExecute(
  runId: number,
  body: BackfillApplyExecuteRequest,
): Promise<BackfillApplyExecuteResponse> {
  const { data } = await api.post<BackfillApplyExecuteResponse>(`${BASE}/${runId}/execute`, body)
  return data
}

export async function listBackfillApplyRuns(month: string): Promise<BackfillApplyRun[]> {
  const { data } = await api.get<BackfillApplyRun[] | { runs: BackfillApplyRun[] }>(`${BASE}/runs`, {
    params: { month },
  })
  if (Array.isArray(data)) return data
  return data.runs ?? []
}

export async function getBackfillApplyRun(runId: number): Promise<BackfillApplyRun> {
  const { data } = await api.get<BackfillApplyRun>(`${BASE}/runs/${runId}`)
  return normalizeBackfillApplyRun(data)
}

export async function abortBackfillApplyRun(runId: number): Promise<void> {
  await api.post(`${BASE}/${runId}/abort`)
}
